/**
 * Operator-plane entitlements provider + hooks (ADR-0041 §11). Loads
 * `GET /api/admin/entitlements` ONCE (via TanStack Query, cached) so nav gating + usage
 * badges read from one place. **The backend is the real gate; this is UX.** Fail-OPEN:
 * while loading or on error, everything is granted (never flash a locked UI on a hiccup).
 *
 * Primitives: `useFeature(key)` → bool, `useCap(metric)` → `{used, limit, atCap, nearCap}`.
 */
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { type Entitlements, entitlementsApi } from "./api-entitlements";

interface CapInfo {
  used: number | null;
  limit: number | null; // null = unlimited
  atCap: boolean;
  nearCap: boolean; // ≥ 80% of the limit
  ratio: number | null; // used/limit, null when unlimited/unmetered
}

const NEAR_CAP_RATIO = 0.8;

const EntitlementsContext = React.createContext<Entitlements | null>(null);

export function EntitlementsProvider({ children }: { children: React.ReactNode }) {
  const q = useQuery({
    queryKey: ["entitlements"],
    queryFn: () => entitlementsApi.get(),
    staleTime: 60_000,
    retry: false,
  });
  // Fail-open: until we know otherwise, the tenant has everything (no locked flash).
  return (
    <EntitlementsContext.Provider value={q.data ?? null}>{children}</EntitlementsContext.Provider>
  );
}

/** True if the current tenant has feature `key`. Fail-OPEN (true) while unknown. */
export function useFeature(key: string): boolean {
  const ent = React.useContext(EntitlementsContext);
  if (!ent || ent.unlimited) return true;
  return ent.features[key] ?? true;
}

/** Usage vs cap for a numeric metric. `limit === null` = unlimited (never atCap). */
export function useCap(metric: string): CapInfo {
  const ent = React.useContext(EntitlementsContext);
  if (!ent || ent.unlimited) {
    return { used: null, limit: null, atCap: false, nearCap: false, ratio: null };
  }
  const lu = ent.limits[metric];
  const limit = lu?.limit ?? null;
  const used = lu?.used ?? null;
  if (limit === null || used === null) {
    return { used, limit, atCap: false, nearCap: false, ratio: null };
  }
  const ratio = limit === 0 ? 1 : used / limit;
  return {
    used,
    limit,
    atCap: used >= limit,
    nearCap: ratio >= NEAR_CAP_RATIO,
    ratio,
  };
}
